/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import { useTranslation } from 'react-i18next';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import useSessionStore from 'stores/useSessionStore';
import { PackingList, SetupExcpParam } from 'models/setup/excp/excp';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { SetupProject } from 'models/setup/SetupProject';
import { DataType } from '@grapecity/wijmo';
import { hasRole } from 'utils/SessionUtil';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import { Button, Tooltip } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import _ from 'lodash';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { useCommonModal } from 'hooks/useCommonModal';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import { downloadExcelTemplatesPost, uploadExcelUtMatrixTemplates } from 'apis/common/Excel';
import { FileTypeName } from 'models/common/Common';
import { CrudCode } from 'models/common/Edit';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import {
  getSetupShipmentList,
  saveShipmentList,
  getProjectShipmentCnt,
} from 'apis/setup/excp/shipment';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import * as input from '@grapecity/wijmo.input';
import { CommonUtil } from 'utils/CommonUtil';
import { useLocation } from 'react-router-dom';
import GridSettingButtons from 'components/grids/GridSettingButtons';
import { findDisplayProject } from 'apis/setup/SetupProject';

interface ShipmentParam {
  elmPrjId?: string;
}

const SetupShipmentManagementPage = () => {
  const { t } = useTranslation();
  const { userId, deptCd, deptNm, elmPrjId, ensolYn } = useSessionStore();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const locationState = useLocation().state as ShipmentParam | null;

  const gridRef = useRef<any>();
  const gridUiRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [errors, setErrors] = useState<any>({});
  const [rowData, setRowData] = useState<PackingList[]>([]); //그리드
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>(); // 플랜트
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]); //프로젝트
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule[]>([]); //협력사
  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);
  const [projectCnt, setProjectCnt] = useState<SetupExcpParam>();

  const isEmailFormat = () => {
    if (!userId) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(userId);
  };

  const initSearchParam = {
    bildPlntCd: [],
    elmPrjId: elmPrjId || '',
    excpId: ensolYn === 'Y' ? '' : deptCd,
    excpNm: ensolYn === 'Y' ? '' : deptNm,
    searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
  };

  const [excpParam, setExcpParam] = useState<SetupExcpParam>({ ...initSearchParam });

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (excpParam?.bildPlntCds || []).length > 0
          ? excpParam.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, excpParam?.bildPlntCds]);

  const optionsPrjExcp = useMemo(() => {
    return (prjExcpData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.excpId,
          cmnCdNm: cur.excpNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjExcpData]);

  const initCodes = async () => {
    const bildPlntOption: Code[] = await getCommonCodeNames('FA_SU_PLNT_CD'); // 플랜트코드
    setBildPlntCode(bildPlntOption);

    findDisplayProject().then((result) => {
      setPrjMasterData(result || []);
      if (!excpParam.elmPrjId && result?.length > 0) {
        handleOnChange('elmPrjId', result[0].elmPrjId);
      }
    });
  };

  const handleOnChange = (name, value) => {
    setExcpParam((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const btnReload = () => {
    const isExcp = !hasRole('FA_DOVI') && isEmailFormat();
    setExcpParam((prev) => ({
      bildPlntCd: [],
      elmPrjId: '',
      excpId: isExcp ? prev.excpId : '',
      excpNm: isExcp ? prev.excpNm : '',
      searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
    }));
  };

  const btnSearch = () => {
    setErrors({
      elmPrjId: _.isEmpty(excpParam.elmPrjId),
    });

    if (_.isEmpty(excpParam.elmPrjId)) {
      return;
    }
    openLoading(true);
    getItemCnt();
    getSetupShipmentList(excpParam)
      .then((result: PackingList[]) => {
        setRowData(result);
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const btnExcelExport = () => {
    CommonUtil.exportWijmoExcelGrid(
      flexRef,
      t('ip.label.선적 List 등록/조회', '선적 List 등록/조회'),
      t('ip.label.선적 List', '선적 List')
    );
  };

  const btnTemplateDownload = async () => {
    // [26.03.26] 템플릿 다운로드 시 프로젝트 선택 필수
    if (!excpParam.elmPrjId) {
      btnSearch();
      return;
    }
    openLoading(true);
    downloadExcelTemplatesPost(
      FileTypeName.TPL_SETUP_PKG_LIST,
      excpParam.elmPrjId ?? '',
      [],
      excpParam?.excpId ?? ''
    ).finally(() => openLoading(false));
  };

  const btnTemplateUpload = async (file) => {
    openLoading(true);
    uploadExcelUtMatrixTemplates(
      file,
      'TPL_SETUP_PKG_REGISTER',
      'NORMAL',
      'TB_EELMB_PRJ_CTNR_M',
      excpParam.elmPrjId,
      'PRJ_PKG_REGISTER'
    )
      .then((response) => {
        if (response?.data.x_result == 'OK') {
          openMessageBar({
            type: 'confirm',
            content: t('setup.label.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
          });
          btnSearch();
        } else {
          setExcelValidCondition(response?.data.p_xls_upload_id);
          setExcelValidModalOpen(true);
        }
      })
      .finally(() => {
        openLoading(false);
      });
  };

  const getItemCnt = async () => {
    getProjectShipmentCnt(excpParam).then((result: PackingList) => {
      if (result) {
        setProjectCnt(result);
      }
    });
  };

  const btnDelRow = () => {
    const selectedRowNodes = gridRef.current.selectedRows;
    const selectedIds = selectedRowNodes
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData || []);
  };

  const layoutDefinition = [
    {
      binding: 'crudKey',
      header: String(t('com.label.상태', '상태')),
      minWidth: 43,
      width: 43,
      align: 'center',
      isReadOnly: true,
      cellTemplate: GridStatusCellTemplate,
      visible: isEmailFormat(),
    },
    {
      binding: 'excpNm',
      header: String(t('com.label.협력업체명', '협력업체명')),
      width: 140,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'ctnrSrno',
      header: String(t('com.label.박스 No', 'CNTR No')),
      width: 150,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'pakBoxNo',
      header: String(t('com.label.박스 No', 'Box No')),
      width: 150,
      align: 'center',
      isReadOnly: true,
    },
    {
      header: String(t('com.label.FOB', 'FOB')),
      columns: [
        {
          binding: 'shipNo',
          header: String(t('com.label.Shipment No', 'Shipment No')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'poNo',
          header: String(t('com.label.PO No', 'PO No')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'hblNo',
          header: String(t('com.label.HBL No', 'HBL No')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'mblNo',
          header: String(t('com.label.MBL No', 'MBL No')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'vslCoNm',
          header: String(t('com.label.선사', '선사')),
          width: 150,
          align: 'center',
        },
      ],
    },
    {
      header: String(t('com.label.POL', 'POL')),
      columns: [
        {
          binding: 'shipPotNm',
          header: String(t('com.label.출발항', '출발항')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'shipPotUpdEtdDt',
          header: String(t('com.label.출항예정일', '출항예정일')),
          width: 150,
          align: 'center',
          cssClass: 'WijmoDate',
          format: 'yyyy.MM.dd',
          dataType: DataType.String,
          editor: new input.InputDate(document.createElement('div'), {
            format: 'yyyy.MM.dd',
            min: new Date(),
          }),
        },
        {
          binding: 'shipPotAtdDt',
          header: String(t('com.label.실제출항일', '실제출항일')),
          width: 150,
          align: 'center',
          cssClass: 'WijmoDate',
          format: 'yyyy.MM.dd',
          dataType: DataType.String,
          editor: new input.InputDate(document.createElement('div'), {
            format: 'yyyy.MM.dd',
            min: new Date(),
            isRequired: false,
          }),
        },
      ],
    },
    {
      header: String(t('com.label.POD', 'POD')),
      columns: [
        {
          binding: 'arivPotNm',
          header: String(t('com.label.도착항', '도착항')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'arivPotUpdEtaDt',
          header: String(t('com.label.도착 예정일', '도착 예정일')),
          width: 150,
          align: 'center',
          cssClass: 'WijmoDate',
          format: 'yyyy.MM.dd',
          dataType: DataType.String,
          editor: new input.InputDate(document.createElement('div'), {
            format: 'yyyy.MM.dd',
            min: new Date(),
          }),
        },
        {
          binding: 'arivPotAtaDt',
          header: String(t('com.label.실제 도착일', '실제 도착일')),
          width: 150,
          align: 'center',
          cssClass: 'WijmoDate',
          format: 'yyyy.MM.dd',
          dataType: DataType.String,
          editor: new input.InputDate(document.createElement('div'), {
            format: 'yyyy.MM.dd',
            min: new Date(),
            isRequired: false,
          }),
        },
      ],
    },
    {
      header: String(t('com.label.통관', '통관')),
      columns: [
        {
          binding: 'atcpCtcCpltDt',
          header: String(t('com.label.예상통관완료일', '예상통관완료일')),
          width: 150,
          align: 'center',
          cssClass: 'WijmoDate',
          format: 'yyyy.MM.dd',
          dataType: DataType.String,
          editor: new input.InputDate(document.createElement('div'), {
            format: 'yyyy.MM.dd',
            min: new Date(),
          }),
        },
        {
          binding: 'atulCtcCpltDt',
          header: String(t('com.label.실제통관완료일', '실제통관완료일')),
          width: 150,
          align: 'center',
          cssClass: 'WijmoDate',
          format: 'yyyy.MM.dd',
          dataType: DataType.String,
          editor: new input.InputDate(document.createElement('div'), {
            format: 'yyyy.MM.dd',
            min: new Date(),
            isRequired: false,
          }),
        },
      ],
    },
    {
      header: String(t('com.label.Site', 'Site')),
      columns: [
        {
          binding: 'shdsNm',
          header: String(t('com.label.Site 명', 'Site 명')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'shdsUpdEtaDt',
          header: String(t('com.label.도착 예정일', '도착 예정일')),
          width: 150,
          align: 'center',
          cssClass: 'WijmoDate',
          format: 'yyyy.MM.dd',
          dataType: DataType.String,
          editor: new input.InputDate(document.createElement('div'), {
            format: 'yyyy.MM.dd',
            min: new Date(),
          }),
        },
        {
          binding: 'shdsAtaDt',
          header: String(t('com.label.실제 도착일', '실제 도착일')),
          width: 150,
          align: 'center',
          cssClass: 'WijmoDate',
          format: 'yyyy.MM.dd',
          dataType: DataType.String,
          editor: new input.InputDate(document.createElement('div'), {
            format: 'yyyy.MM.dd',
            min: new Date(),
            isRequired: false,
          }),
        },
      ],
    },
    {
      binding: 'ctnrTpCd',
      header: String(t('com.label.CNTR Type', 'CNTR Type')),
      width: 150,
      align: 'center',
    },
    {
      header: String(t('com.label.Box Size', 'Box Size')),
      columns: [
        {
          binding: 'ctnrLen',
          header: String(t('com.label.L(mm)', 'L(mm)')),
          width: 150,
          align: 'center',
          dataType: 'Number',
        },
        {
          binding: 'ctnrBth',
          header: String(t('com.label.W(mm)', 'W(mm)')),
          width: 150,
          align: 'center',
          dataType: 'Number',
        },
        {
          binding: 'ctnrHigt',
          header: String(t('com.label.H(mm)', 'H(mm)')),
          width: 150,
          align: 'center',
          dataType: 'Number',
        },
        {
          binding: 'ctnrCapa',
          header: String(t('com.label.CBM', 'CBM')),
          width: 150,
          align: 'center',
          dataType: 'Number',
        },
      ],
    },
    {
      binding: 'dataUpdUserNm',
      header: String(t('com.label.수정자', '수정자')),
      width: 150,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'dataUpdDtm',
      header: String(t('com.label.수정일자', '수정일자')),
      width: 150,
      align: 'center',
      isReadOnly: true,
    },
  ];

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;
  };

  const validateSetup = (itemData: PackingList[]): string | null => {
    if (!itemData || itemData.length === 0) {
      return t('setup.msg.저장할 데이터가 없습니다.', '저장할 데이터가 없습니다.');
    }

    const msgs: string[] = [];

    itemData.forEach((row, rowIndex) => {
      if (row.crudKey !== CrudCode.UPDATE) return;

      const line = rowIndex + 1;

      if (_.isEmpty(row.shipPotUpdEtdDt)) {
        msgs.push(
          `${line}행 - ` +
            t('setup.label.출항예정일을 입력해 주세요.', '출항예정일을 입력해 주세요.')
        );
      }
    });

    if (!msgs.length) return null;
    return msgs.join('\n');
  };

  const btnSave = () => {
    const msg = validateSetup(rowData);
    if (msg) {
      openMessageBar({ type: 'error', content: msg });
      return false;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: async () => {
        const response = await saveShipmentList({
          packingList: rowData,
        });
        if (response == null || response?.successOrNot === 'N') {
          openMessageBar({
            type: 'error',
            content: t('com.label.처리 중 오류가 발생했습니다.', '처리 중 오류가 발생했습니다.'),
          });
          return;
        }
        openMessageBar({
          type: 'confirm',
          content: t('com.label.저장되었습니다.', '저장되었습니다.'),
        });
        btnSearch();
      },
    });
  };

  useEffect(() => {
    initCodes();
  }, []);

  useEffect(() => {
    if (excpParam?.elmPrjId) {
      findExcpByElmPrjId(excpParam?.elmPrjId).then((result: SetupSchedule[]) => {
        setPrjExcpData(result);
      });
      btnSearch();
    } else {
      setPrjExcpData([]);
    }
  }, [excpParam?.elmPrjId, excpParam?.excpId]);

  useEffect(() => {
    if (!_.isEmpty(locationState)) {
      setExcpParam({
        ...excpParam,
        elmPrjId: locationState?.elmPrjId,
      });
    }
  }, [locationState]);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('setup.label.플랜트', '플랜트')}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={bildPlntCode || []}
                  defaultValue={excpParam.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          excpParam?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                      if (!isEmailFormat()) handleOnChange('excpId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span className={'dot'}>{t('setup.label.프로젝트명', '프로젝트명')}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster}
                  defaultValue={
                    locationState?.elmPrjId ? locationState?.elmPrjId : excpParam?.elmPrjId
                  }
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                    if (!isEmailFormat()) handleOnChange('excpId', '');
                  }}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                  )}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.협력업체명', '협력업체명')}</label>
                {hasRole('FA_DOVI') || !isEmailFormat() ? (
                  <ComboBox
                    placeholder={String(t('com.label.전체', '전체'))}
                    options={optionsPrjExcp}
                    defaultValue={excpParam?.excpId}
                    onChange={(value) => {
                      handleOnChange('excpId', value);
                    }}
                  />
                ) : (
                  <div className="txt">
                    <span>{excpParam?.excpNm}</span>
                  </div>
                )}
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button css={IconButton.button} className="reload" disableRipple onClick={btnReload} />
            <Button css={IconButton.button} className="find" disableRipple onClick={btnSearch}>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.선적 List', '선적 List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
          {projectCnt && (
            <Tooltip
              title={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    padding: '4px',
                  }}
                >
                  <span style={{ marginRight: '8px' }}>
                    {t('setup.label.컨테이너 선적율', '컨테이너 선적율') + ' = '}
                  </span>
                  <div style={{ textAlign: 'center' }}>
                    <span>
                      {t(
                        'setup.label.등록된 Shipment No(선적번호) 수량',
                        '등록된 Shipment No(선적번호) 수량'
                      )}
                    </span>
                    <div
                      style={{
                        borderTop: '1px solid white',
                        width: '100%',
                        margin: '2px 0',
                      }}
                    />
                    <span>
                      {t(
                        'setup.label.등록된 총 CNTR 수량 (중복제외)',
                        '등록된 총 CNTR 수량 (중복제외)'
                      )}
                    </span>
                  </div>
                </div>
              }
              placement="top"
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 'none',
                  },
                },
              }}
            >
              <div className="info warning">
                {t('setup.label.컨테이너 선적율', '컨테이너 선적율')} : {projectCnt?.summary}
              </div>
            </Tooltip>
          )}
        </SubTitleGroup>
        <ControlBtnGroup>
          {isEmailFormat() && (
            <>
              <Button
                css={IconButton.button}
                className="Exceldown"
                onClick={btnTemplateDownload}
                disableRipple
              >
                {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
              </Button>
              <ExcelUploadButton onCallback={btnTemplateUpload} />
              <Button css={IconButton.button} className="delRow" onClick={btnDelRow} disableRipple>
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={'shipment-grid-id'}
            layoutDefinition={layoutDefinition}
            changeShownColumns={(result) => {
              gridUiRef.current?.changeColumn?.(result);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id="shipment-grid-id"
        ref={gridUiRef}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        height={530}
        isReadOnly={!isEmailFormat()}
        isSelector={isEmailFormat()}
      />
      {isEmailFormat() && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
      {isExcelValidModalOpen && (
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}
    </>
  );
};

export default SetupShipmentManagementPage;
